import { COUNTRIES_CALLING_CODES } from "../data/countries";

export type ParsedContactPhone = {
  phoneNumber: string;
  phoneNumberPrefix: string;
};

const MIN_NATIONAL_LENGTH = 8;
const DEFAULT_PREFIXES = COUNTRIES_CALLING_CODES.map((country) => country.prefix);

function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

function shouldStripConcatenatedPrefix(national: string, prefixDigits: string): boolean {
  if (!prefixDigits || !national.startsWith(prefixDigits)) {
    return false;
  }

  const remainingLength = national.length - prefixDigits.length;
  if (remainingLength < MIN_NATIONAL_LENGTH) {
    return false;
  }

  if (prefixDigits.length === 1 && remainingLength < MIN_NATIONAL_LENGTH + 1) {
    return false;
  }

  return true;
}

export function nationalPhoneNumber(number: string, prefix?: string | null): string {
  const prefixDigits = digitsOnly(prefix ?? "");
  const tokens = (number ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => digitsOnly(token))
    .filter((token) => token.length > 0);

  let national = tokens.filter((token) => !prefixDigits || token !== prefixDigits).join("");

  if (!national) {
    national = digitsOnly(number ?? "");
  }

  if (shouldStripConcatenatedPrefix(national, prefixDigits)) {
    national = national.slice(prefixDigits.length);
  }

  return national;
}

export function parseContactPhone(
  phoneStr: string,
  knownPrefixes: readonly string[] = DEFAULT_PREFIXES,
): ParsedContactPhone {
  if (!phoneStr?.trim()) {
    return { phoneNumber: "", phoneNumberPrefix: "" };
  }

  const tokens = phoneStr
    .trim()
    .replace(/^\+/, "")
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => digitsOnly(token))
    .filter((token) => token.length > 0);

  if (tokens.length >= 2) {
    const phoneNumberPrefix = tokens[0] ?? "";
    const rest = tokens
      .slice(1)
      .filter((token) => token !== phoneNumberPrefix)
      .join("");

    return {
      phoneNumberPrefix,
      phoneNumber: nationalPhoneNumber(rest, phoneNumberPrefix),
    };
  }

  const cleaned = tokens[0] ?? digitsOnly(phoneStr);
  const prefixes = [...knownPrefixes].sort((a, b) => b.length - a.length);

  for (const prefix of prefixes) {
    const prefixDigits = digitsOnly(prefix);
    if (shouldStripConcatenatedPrefix(cleaned, prefixDigits)) {
      return {
        phoneNumberPrefix: prefixDigits,
        phoneNumber: cleaned.slice(prefixDigits.length),
      };
    }
  }

  return { phoneNumberPrefix: "", phoneNumber: cleaned };
}

export function formatContactPhoneForSave(
  prefix: string | null | undefined,
  number: string,
): string {
  const prefixDigits = digitsOnly(prefix ?? "");
  const national = nationalPhoneNumber(number, prefixDigits);

  if (prefixDigits && national) {
    return `+${prefixDigits} ${national}`;
  }

  return national;
}
